import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, MapPin, ChevronDown, AlertTriangle } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import { PHONES, CONTACT } from '../content';
import { DISTRICT_PAGES } from '../data/districts';
import { getRoadBySlug } from '../data/roads';
import { SERVICE_PAGES } from '../data/services-seo';
import { SEOHead, BreadcrumbSchema } from '../components/SEOHead';
import Breadcrumb from '../components/Breadcrumb';
import FinalCTA from '../components/FinalCTA';
import LiveBadge from '../components/LiveBadge';

const WA_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

export default function RoadPage() {
  const location = useLocation();
  const slug = location.pathname.replace('/', '');
  const road = getRoadBySlug(slug);

  if (!road) return null;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": road.faqs.map(f => ({
      "@type": "Question", "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <main data-testid={`road-page-${road.slug}`} style={{ background: 'var(--bg)' }}>
      <SEOHead path={`/${road.slug}`} title={road.title} description={road.metaDesc} />
      <BreadcrumbSchema items={[
        { name: 'Anasayfa', url: '/' },
        { name: 'Acil Yol Yardım', url: '/acil-yol-yardim' },
        { name: road.name },
      ]} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section style={{ background: 'var(--bg)', paddingTop: 86, borderBottom: '1px solid var(--border)' }}>
        <div className="wrap" style={{ padding: '3.5rem 1.5rem 3rem' }}>
          <Breadcrumb items={[
            { label: 'Anasayfa', to: '/' },
            { label: 'Acil Yol Yardım', to: '/acil-yol-yardim' },
            { label: road.name },
          ]} />

          <LiveBadge text={`${road.name} — 7/24 Aktif`} />

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5 }}
            style={{
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
              fontSize: 'clamp(2rem, 5.5vw, 3.5rem)', color: 'var(--text)',
              lineHeight: 1.05, letterSpacing: '-.04em', margin: '1rem 0 .5rem',
            }}
          >
            {road.h1}
          </motion.h1>
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(1.2rem, 3.5vw, 2rem)', color: 'var(--red)',
            lineHeight: 1.1, letterSpacing: '-.03em', marginBottom: '1rem',
          }}>
            {road.h1Accent}
          </p>
          <p style={{ fontSize: 'clamp(.875rem, 1.5vw, 1rem)', color: 'var(--text2)', lineHeight: 1.7, maxWidth: 600 }}>
            {road.intro}
          </p>

          {/* Emergency CTA */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '.625rem', marginTop: '1.75rem',
            padding: '1.25rem', background: 'rgba(229,51,51,.06)', border: '1px solid rgba(229,51,51,.15)',
            borderRadius: 'var(--rl)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginRight: 'auto' }}>
              <AlertTriangle size={18} style={{ color: 'var(--red)' }} />
              <span style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--text)' }}>Otoyolda mı kaldınız?</span>
            </div>
            <a href={PHONES.main_tel} data-testid="road-call-cta" className="btn btn-primary num ring" style={{ gap: '.5rem' }}>
              <Phone size={15} /> {PHONES.main}
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
              data-gtm-action="whatsapp_click" data-testid="road-whatsapp-cta"
              className="btn btn-green" style={{ gap: '.4rem', fontSize: '.875rem' }}>
              {WA_ICON} WhatsApp Konum Gönder
            </a>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div
            className="blog-content"
            style={{ maxWidth: 800, color: 'var(--text2)', lineHeight: 1.85, fontSize: '.95rem' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(road.content) }}
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
        <div className="wrap">
          <h2 className="h2" style={{ marginBottom: '1.5rem' }}>
            Sık Sorulan <span style={{ color: 'var(--blue)' }}>Sorular</span>
          </h2>
          <div style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            {road.faqs.map((faq, i) => (
              <FAQItem key={faq.q} faq={faq} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* DISTRICTS */}
      <section className="section" style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
        <div className="wrap" style={{ textAlign: 'center' }}>
          <h2 className="h2" style={{ marginBottom: '1.5rem' }}>
            Hangi <span style={{ color: 'var(--green)' }}>İlçedesiniz?</span>
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '.5rem' }}>
            {DISTRICT_PAGES.map(d => (
              <Link key={d.slug} to={`/${d.slug}`} style={{
                display: 'inline-block', background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 100, padding: '.35rem .9rem', fontSize: '.8rem', color: 'var(--text2)',
                textDecoration: 'none', transition: 'border-color .2s, color .2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.color = 'var(--green)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
              >{d.displayName}</Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA headline="Otoyolda Kalma." sub="Hemen Ara — 7/24 Aktifiz." />
    </main>
  );
}

function FAQItem({ faq, index }) {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <div
      data-testid={`road-faq-${index}`}
      style={{
        background: 'var(--card)', border: `1px solid ${isOpen ? 'var(--blue-border)' : 'var(--border)'}`,
        borderRadius: 'var(--r)', overflow: 'hidden', transition: 'border-color .25s', cursor: 'pointer',
      }}
      onClick={() => setIsOpen(!isOpen)}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', gap: '.75rem' }}>
        <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '.88rem', color: 'var(--text)', lineHeight: 1.4, margin: 0 }}>
          {faq.q}
        </h3>
        <ChevronDown size={16} style={{ flexShrink: 0, color: 'var(--muted)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .25s' }} />
      </div>
      {isOpen && (
        <div style={{ padding: '0 1.25rem 1rem', fontSize: '.84rem', color: 'var(--text2)', lineHeight: 1.75 }}>
          {faq.a}
        </div>
      )}
    </div>
  );
}
